/**
 * Document Upload Page
 *
 * Allows company users to upload new documents to the vault:
 * - Step 1: File Selection (drag & drop or file picker)
 * - Step 2: Document Information & Expiry Settings
 * - Step 3: Review & Upload
 * - File validation and preview
 * - Upload progress (mock in Sprint 1)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Input } from '../../components/forms/Input';
import { Textarea } from '../../components/forms/Textarea';
import { Select } from '../../components/forms/Select';
import { FileUpload } from '../../components/forms/FileUpload';
import { Alert } from '../../components/common/Alert';
import {
  Wizard,
  WizardStep,
  WizardNavigation,
  WizardProgress,
  WizardFooter,
} from '../../components/wizard';
import { HelpPanel, HelpSection } from '../../components/help';
import { SuccessScreen } from '../../components/feedback/SuccessScreen';
import { FileUp, FileText, AlertCircle, CheckCircle2 } from 'lucide-react';
import { DocumentCategory } from '../../data/documents';

interface DocumentFormData {
  name: string;
  category: DocumentCategory | '';
  description: string;
  expiryDate: string;
  reminderDays: number;
  files: File[];
}

export const DocumentUploadPage: React.FC = () => {
  const { t } = useTranslation(['common', 'company']);
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [uploadedDocName, setUploadedDocName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState<DocumentFormData>({
    name: '',
    category: '',
    description: '',
    expiryDate: '',
    reminderDays: 30,
    files: [],
  });

  const handleFieldChange = (field: keyof DocumentFormData, value: string | number | File[]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrorMessage('');
  };

  const validateStep1 = (): boolean => {
    if (formData.files.length === 0) {
      setErrorMessage('Please select a file to upload');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!formData.name.trim()) {
      setErrorMessage('Document name is required');
      return false;
    }

    if (!formData.category) {
      setErrorMessage('Document category is required');
      return false;
    }

    setErrorMessage('');
    return true;
  };

  const handleUpload = async () => {
    setUploading(true);
    setErrorMessage('');

    try {
      // Mock upload - simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // In real implementation, this would upload to backend
      console.log('Uploading document:', {
        ...formData,
        fileNames: formData.files.map((f) => f.name),
      });

      // Save document name for success screen
      setUploadedDocName(formData.name);

      // Reset form
      setFormData({
        name: '',
        category: '',
        description: '',
        expiryDate: '',
        reminderDays: 30,
        files: [],
      });

      // Show success screen
      setShowSuccess(true);
    } catch (error) {
      console.error('Upload error:', error);
      setErrorMessage('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveDraft = async () => {
    console.log('Saving document draft...', formData);
    alert('Document information saved as draft');
  };

  const categoryOptions = [
    { value: '', label: 'Select Category' },
    { value: DocumentCategory.LICENSES, label: 'Business License' },
    { value: DocumentCategory.POLICIES, label: 'Halal Policy' },
    { value: DocumentCategory.CERTIFICATIONS, label: 'Certification' },
    { value: DocumentCategory.FACILITY, label: 'Facility Document' },
    { value: DocumentCategory.TRAINING, label: 'Training Record' },
    { value: DocumentCategory.SUPPLIERS, label: 'Supplier Document' },
    { value: DocumentCategory.PRODUCTS, label: 'Product Specification' },
    { value: DocumentCategory.AUDITS, label: 'Audit Report' },
    { value: DocumentCategory.SOPs, label: 'Standard Operating Procedure' },
    { value: DocumentCategory.OTHER, label: 'Other' },
  ];

  const reminderOptions = [
    { value: '7', label: '7 days before' },
    { value: '14', label: '14 days before' },
    { value: '30', label: '30 days before' },
    { value: '60', label: '60 days before' },
    { value: '90', label: '90 days before' },
  ];

  const wizardSteps = [
    { id: 'file', label: 'Select File', validate: validateStep1 },
    { id: 'info', label: 'Document Info', validate: validateStep2 },
    { id: 'review', label: 'Review & Upload' },
  ];

  const renderStep1 = () => (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold">Step 1: Select File</h3>
        <p className="text-sm text-gray-600 mt-1">Choose the document file to upload</p>
      </CardHeader>
      <CardBody>
        <Alert type="info" className="mb-6">
          <div>
            <strong>Upload Guidelines:</strong>
            <ul className="mt-2 ml-4 list-disc space-y-1">
              <li>Accepted formats: PDF, DOC, DOCX, JPG, PNG (max 10MB per file)</li>
              <li>Documents will be reviewed by the certification body before being marked as verified</li>
              <li>Ensure all information is accurate and up-to-date</li>
              <li>Set expiry dates for time-sensitive documents (licenses, certificates, etc.)</li>
            </ul>
          </div>
        </Alert>

        <FileUpload
          variant="dropzone"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          maxSize={10}
          multiple={false}
          onFileSelect={(files) => {
            if (files) {
              handleFieldChange('files', Array.from(files));
            }
          }}
        />

        {formData.files.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700">Selected file:</p>
            <p className="text-sm text-gray-600">{formData.files[0].name}</p>
          </div>
        )}
      </CardBody>
    </Card>
  );

  const renderStep2 = () => (
    <>
      <Card className="mb-6">
        <CardHeader>
          <h3 className="text-lg font-semibold">Step 2: Document Information</h3>
          <p className="text-sm text-gray-600 mt-1">Provide details about the document</p>
        </CardHeader>
        <CardBody>
          <div className="space-y-6">
            <Input
              label={t('company:documents.documentName')}
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="e.g., Business License 2025"
              required
              helperText="Enter a clear, descriptive name for the document"
            />

            <Select
              label="Category"
              value={formData.category}
              options={categoryOptions}
              onChange={(e) => handleFieldChange('category', e.target.value as DocumentCategory)}
              required
              helperText="Select the category that best describes this document"
            />

            <Textarea
              label="Description"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Brief description of the document contents..."
              rows={4}
              maxLength={500}
              helperText="Optional: Provide additional context about this document"
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Expiry Settings</h3>
          <p className="text-sm text-gray-600 mt-1">Optional - Set for time-sensitive documents</p>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label={t('company:documents.expiryDate')}
              type="date"
              value={formData.expiryDate}
              onChange={(e) => handleFieldChange('expiryDate', e.target.value)}
              helperText="Leave empty if document does not expire"
            />

            <Select
              label="Expiry Reminder"
              value={formData.reminderDays.toString()}
              options={reminderOptions}
              onChange={(e) => handleFieldChange('reminderDays', parseInt(e.target.value, 10))}
              disabled={!formData.expiryDate}
              helperText="When to send expiry reminder notification"
            />
          </div>
        </CardBody>
      </Card>
    </>
  );

  const renderStep3 = () => (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold">Step 3: Review & Upload</h3>
        <p className="text-sm text-gray-600 mt-1">Review your document information before uploading</p>
      </CardHeader>
      <CardBody>
        <div className="space-y-6">
          <Alert type="warning">
            Please review all information carefully. Once uploaded, the document will be submitted for review.
          </Alert>

          <div>
            <h4 className="font-medium mb-3">Document Summary</h4>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">File Name</dt>
                <dd className="mt-1 text-sm">{formData.files[0]?.name || 'No file selected'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">File Size</dt>
                <dd className="mt-1 text-sm">
                  {formData.files[0] ? (formData.files[0].size / 1024 / 1024).toFixed(2) + ' MB' : 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Document Name</dt>
                <dd className="mt-1 text-sm">{formData.name || 'Not provided'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Category</dt>
                <dd className="mt-1 text-sm">
                  {categoryOptions.find((opt) => opt.value === formData.category)?.label || 'Not selected'}
                </dd>
              </div>
              {formData.description && (
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Description</dt>
                  <dd className="mt-1 text-sm">{formData.description}</dd>
                </div>
              )}
              {formData.expiryDate && (
                <>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Expiry Date</dt>
                    <dd className="mt-1 text-sm">{formData.expiryDate}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Reminder</dt>
                    <dd className="mt-1 text-sm">
                      {reminderOptions.find((opt) => opt.value === formData.reminderDays.toString())?.label}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  // Show success screen after upload
  if (showSuccess) {
    return (
      <PageLayout
        pageTitle="Success!"
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('company:dashboard.title'), href: '/company/dashboard' },
          { label: t('company:documents.title'), href: '/company/documents' },
          { label: 'Upload Success' },
        ]}
      >
        <SuccessScreen
          title="Document Uploaded Successfully!"
          message={`"${uploadedDocName}" has been uploaded to your document vault and is now pending review by the certification body. You'll receive a notification once it's been verified.`}
          primaryAction={{
            label: 'Upload Another Document',
            onClick: () => setShowSuccess(false),
          }}
          secondaryAction={{
            label: 'View All Documents',
            onClick: () => navigate('/company/documents'),
          }}
          showAnimation={true}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={t('company:documents.uploadDocument')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('company:dashboard.title'), href: '/company/dashboard' },
        { label: t('company:documents.title'), href: '/company/documents' },
        { label: t('company:documents.uploadDocument') },
      ]}
    >
      {errorMessage && (
        <Alert type="error" title="Error" dismissible onDismiss={() => setErrorMessage('')} className="mb-6">
          {errorMessage}
        </Alert>
      )}

      <div className="flex gap-6">
        <div className="flex-1">
          <Wizard steps={wizardSteps} onComplete={handleUpload} onSaveDraft={handleSaveDraft}>
            <div className="mb-6">
              <WizardProgress showPercentage showStepCount />
            </div>

            <div className="mb-6">
              <WizardNavigation />
            </div>

            <WizardStep>{renderStep1()}</WizardStep>
            <WizardStep>{renderStep2()}</WizardStep>
            <WizardStep>{renderStep3()}</WizardStep>

            <WizardFooter
              nextLabel="Next →"
              backLabel="← Previous"
              completeLabel={uploading ? 'Uploading...' : 'Upload Document'}
              saveDraftLabel="Save for Later"
              showSaveDraft
            />
          </Wizard>
        </div>

        <HelpPanel title="Upload Help" sticky>
          <HelpSection
            icon={FileUp}
            title="File Selection"
            description="Select the document file from your computer. Supported formats include PDF, Word documents, and images."
            variant="primary"
          />

          <HelpSection
            icon={FileText}
            title="Document Categories"
            description="Choose the appropriate category for your document. This helps organize your document vault and ensures compliance."
            collapsible
            defaultCollapsed
          >
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Licenses: Official government permits</li>
              <li>Policies: Company procedures and rules</li>
              <li>Certifications: Third-party verifications</li>
              <li>SOPs: Standard operating procedures</li>
            </ul>
          </HelpSection>

          <HelpSection
            icon={AlertCircle}
            title="Expiry Reminders"
            description="Set expiry dates and reminders for time-sensitive documents to ensure they remain current."
            variant="warning"
          />

          <HelpSection
            icon={CheckCircle2}
            title="Document Review"
            description="All uploaded documents are reviewed by the certification body to ensure they meet halal compliance standards."
            variant="success"
            cta={{
              label: 'Learn more about document requirements',
              href: '#',
            }}
          />
        </HelpPanel>
      </div>
    </PageLayout>
  );
};

export default DocumentUploadPage;
